import { string } from 'fp-ts';
import { LocalizedTypography } from '@mews-commander/ui';
import { Typography, Icon, IconName, Body } from '@optimus-web/core';
import { TimeUnit, UnicodeEntity } from '@mews/enums';
import { LocalizedText, LocalizedTextProps, lt, matchTimeUnitKey } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Amount } from '@mews-commander/data-access';
import {
    ADVANCE_EXCEPTION_TYPE,
    LENGTH_EXCEPTION_TYPE,
    PRICE_EXCEPTION_TYPE,
    RESERVATION_COUNT_EXCEPTION_TYPE,
} from '../../constants';
import { getSecondaryTimeUnit } from '../../utils';
import { AmountRangeText, NumberRangeText } from './ExceptionRangeText';

interface WithRange<T> {
    range: { min?: T; max?: T };
}

interface WithsecondaryRange<T> {
    secondaryRange?: { min?: T; max?: T };
}

interface ExceptionItemProps extends WithRange<number | Amount>, WithsecondaryRange<number> {
    type:
        | typeof ADVANCE_EXCEPTION_TYPE
        | typeof LENGTH_EXCEPTION_TYPE
        | typeof PRICE_EXCEPTION_TYPE
        | typeof RESERVATION_COUNT_EXCEPTION_TYPE;
    isServiceDayUse?: boolean;
    timeUnit: TimeUnit;
    showIcon?: boolean;
}

const exceptionTextKeyMap = {
    [ADVANCE_EXCEPTION_TYPE]: TextKey.Advance,
    [LENGTH_EXCEPTION_TYPE]: TextKey.Length,
    [PRICE_EXCEPTION_TYPE]: TextKey.Price,
    [RESERVATION_COUNT_EXCEPTION_TYPE]: TextKey.ReservationCount,
};

const renderRangeText = (props: ExceptionItemProps) => {
    switch (props.type) {
        case 'advance':
            return (
                <NumberRangeText
                    {...props.range}
                    minDataTestId="minAdvance-exception"
                    maxDataTestId="maxAdvance-exception"
                />
            );
        case 'length':
            return (
                <NumberRangeText
                    {...props.range}
                    minDataTestId="minLength-exception"
                    maxDataTestId="maxLength-exception"
                />
            );
        case 'reservationCount':
            return (
                <NumberRangeText
                    {...props.range}
                    minDataTestId="minReservationCount-exception"
                    maxDataTestId="maxReservationCount-exception"
                />
            );
        case 'price':
            return (
                <AmountRangeText
                    {...props.range}
                    minDataTestId="minPrice-exception"
                    maxDataTestId="maxPrice-exception"
                />
            );
    }
};

const isMaxRangeOne = (range: WithRange<number | Amount>['range']) => range.max !== undefined && range.max === 1;
const isMinRangeOne = (range: WithRange<number | Amount>['range']) => range.min !== undefined && range.min === 1;

const renderRangeSuffix = (props: ExceptionItemProps) => {
    switch (props.type) {
        case 'advance':
            return <LocalizedText textKey={isMaxRangeOne(props.range) ? TextKey.Day : TextKey.DayPlural} />;
        case 'length': {
            const { isServiceDayUse, range, timeUnit } = props;
            const textKey = matchTimeUnitKey({ isServiceDayUse, timeUnit, plural: !isMaxRangeOne(range) });
            return textKey ? <LocalizedText textKey={textKey} /> : null;
        }
        case 'reservationCount':
            return (
                <LocalizedText textKey={isMaxRangeOne(props.range) ? TextKey.Reservation : TextKey.ReservationPlural} />
            );
        default:
            return string.empty;
    }
};

interface CreateKey {
    range: WithRange<number | Amount>['range'];
    method: 'min' | 'max';
    timeUnit: TimeUnit;
    isServiceDayUse?: boolean;
}

const createKey = ({ range, method, isServiceDayUse, timeUnit }: CreateKey) => {
    switch (method) {
        case 'min':
            return matchTimeUnitKey({ isServiceDayUse, timeUnit, plural: !isMinRangeOne(range) });
        case 'max':
            return matchTimeUnitKey({ isServiceDayUse, timeUnit, plural: !isMaxRangeOne(range) });
    }
};

const renderMultiTimeUnitRange = ({ range, timeUnit, secondaryRange, isServiceDayUse }: ExceptionItemProps) => {
    if (!timeUnit) return;
    const secondaryTimeUnit = getSecondaryTimeUnit(timeUnit);
    const defaultMin = !range?.min && secondaryRange?.min ? null : 0;
    const hasSecondaryRange = secondaryTimeUnit && secondaryRange;

    const primaryMinKey = range && createKey({ range, method: 'min', isServiceDayUse, timeUnit });
    const primaryMaxKey = range && createKey({ range, method: 'max', isServiceDayUse, timeUnit });
    const secondaryMinKey =
        hasSecondaryRange &&
        createKey({ range: secondaryRange, method: 'min', timeUnit: secondaryTimeUnit, isServiceDayUse: true });
    const secondaryMaxKey =
        hasSecondaryRange &&
        createKey({ range: secondaryRange, method: 'max', timeUnit: secondaryTimeUnit, isServiceDayUse: true });

    const isMinAndMaxEqual = range?.min === range?.max && secondaryRange?.min === secondaryRange?.max;

    return (
        <Typography textStyle="bodySmall" as="div">
            {range?.min ? (
                <>
                    {range.min}
                    {UnicodeEntity.Space}
                    {primaryMinKey && lt(primaryMinKey)}
                    {UnicodeEntity.Space}
                </>
            ) : (
                defaultMin
            )}
            {secondaryRange?.min ? (
                <>
                    {secondaryRange?.min}
                    {UnicodeEntity.Space}
                    {secondaryMinKey && lt(secondaryMinKey)}
                </>
            ) : null}
            {!isMinAndMaxEqual ? (
                <>
                    {UnicodeEntity.Space}
                    {range?.max || secondaryRange?.max ? UnicodeEntity.Dash : UnicodeEntity.PlusSign}
                    {UnicodeEntity.Space}
                </>
            ) : null}
            {range?.max && !isMinAndMaxEqual ? (
                <>
                    {range.max}
                    {UnicodeEntity.Space}
                    {primaryMaxKey && lt(primaryMaxKey)}
                    {UnicodeEntity.Space}
                </>
            ) : null}
            {secondaryRange?.max && !isMinAndMaxEqual ? (
                <>
                    {secondaryRange.max}
                    {UnicodeEntity.Space}
                    {secondaryMaxKey && lt(secondaryMaxKey)}
                </>
            ) : null}
        </Typography>
    );
};

export const exceptionItemFactory = ({
    ExceptionItemWrapper = Body,
    LabelElement = (props: LocalizedTextProps) => <LocalizedTypography {...props} textStyle="bodySmall" as="div" />,
    ValueElement = Typography,
}: $TSFixMe = {}) => {
    const ExceptionItem = ({ showIcon = false, ...props }: ExceptionItemProps) => (
        <ExceptionItemWrapper>
            {showIcon ? <Icon value={IconName.Problematic} variant="warning" size="small" /> : null}
            <LabelElement textKey={exceptionTextKeyMap[props.type]} />
            <ValueElement variant="secondary" textStyle="bodySmall">
                {props.secondaryRange ? (
                    renderMultiTimeUnitRange(props)
                ) : (
                    <>
                        {renderRangeText(props)}
                        {UnicodeEntity.NoBreakSpace}
                        {renderRangeSuffix(props)}
                    </>
                )}
            </ValueElement>
        </ExceptionItemWrapper>
    );

    return ExceptionItem;
};

export default exceptionItemFactory();
